import "./PoolOverview.scss"
import basePoolHeader from "../assets/base-pool-header.svg"
import metaPoolHeader from "../assets/meta-pool-header.svg"
import { Box, Grid, Tooltip } from "@mui/material"
import {
  POOLS_MAP,
  REWARD_TOKENS,
  PoolName,
  PoolTypes,
  TOKENS_MAP,
  isMetaPool,
  CRO,
  ATOM,
} from "../constants"
import usePoolData from "../hooks/usePoolData"
import React, { ReactElement, useCallback } from "react"
import {
  formatBNToShortString,
  formatBNToString,
  formatNumberToCompactString,
  formatApr,
  formatBoostedApr,
} from "../utils"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { Zero } from "@ethersproject/constants"
import { getAddress } from "src/utils/addressHelpers"
import { useTranslation } from "react-i18next"
import RocketIcon from "src/assets/icons/rocket.svg"
import { AppState } from "../state"
import { SwapFarmStat } from "src/state/application"
import CustomCard from "./CustomCard"
import Text from "./Text"
import BoostedTag from "./BoostFarming/BoostedTag"

interface Props {
  poolName: PoolName
}

export default function PoolOverview({ poolName }: Props): ReactElement | null {
  const { t } = useTranslation()
  const pool = POOLS_MAP[poolName]
  const { type: poolType, lpToken, route } = pool
  const formattedDecimals = poolType === PoolTypes.USD ? 2 : 4
  const [poolData, userShareData, boostData] = usePoolData(poolName)
  const boostFactor = boostData?.boostFactor
    ? parseFloat(formatBNToString(boostData.boostFactor, 18))
    : 1
  const maxBoostFactor = boostData?.maxBoostFactor
    ? parseFloat(formatBNToString(boostData.maxBoostFactor, 18))
    : 1
  const swapFarmStat = useSelector<AppState, SwapFarmStat | undefined>(
    (state) => state.application?.swapFarmStats?.[poolName],
  )
  const { tokenPricesUSD } = useSelector((state: AppState) => state.application)

  const poolVolumeNum = poolData.volume
    ? parseFloat(formatBNToString(poolData.volume, 18))
    : 0
  let tokenPrice = 1
  if (poolType === PoolTypes.CRO) {
    tokenPrice = tokenPricesUSD ? tokenPricesUSD[CRO.symbol] : 0
  } else if (poolType === PoolTypes.ATOM) {
    tokenPrice = tokenPricesUSD ? tokenPricesUSD[ATOM.symbol] : 0
  }
  const poolVolumeUSD = poolVolumeNum * tokenPrice
  const rewardData = swapFarmStat?.rewardData ?? []

  const otherRewards = (
    rewardData.map((reward) => {
      const symbol = REWARD_TOKENS.find(
        (token) =>
          getAddress(token.addresses) === getAddress(reward.token.addresses),
      )?.symbol
      return {
        symbol,
        value:
          symbol && reward.rewardPerDay
            ? `${formatNumberToCompactString(reward.rewardPerDay)}`
            : "-",
      }
    }) as {
      symbol: string
      value: string
    }[]
  ).filter((reward) => reward.symbol !== undefined)

  const formattedRewards =
    !swapFarmStat?.ferPerDay && otherRewards?.length === 0
      ? [
          {
            symbol: "-",
            value: "",
          },
        ]
      : swapFarmStat?.ferPerDay
      ? [
          {
            symbol: "FER",
            value: formatNumberToCompactString(swapFarmStat?.ferPerDay),
          },
        ].concat(otherRewards)
      : otherRewards
  const formattedData = {
    name: poolData.name,
    reserve:
      poolData.reserve && poolData.reserve.gt("0")
        ? formatBNToShortString(poolData.reserve, 18, true)
        : "-",
    apr: formatApr(poolData.apr),
    boostedApr: formatBoostedApr(poolData.apr, poolData.ferroApr, boostFactor),
    maxApr: formatBoostedApr(poolData.apr, poolData.ferroApr, maxBoostFactor),
    volume:
      poolVolumeUSD && poolVolumeUSD > 0
        ? `$${formatNumberToCompactString(poolVolumeUSD)}`
        : "-",
    userBalanceUSD: formatBNToShortString(
      userShareData?.usdBalance || Zero,
      18,
    ),
    rewardsPerDay: formattedRewards,
    tokens: poolData.tokens.map((coin) => {
      const token = TOKENS_MAP[coin.symbol]
      return {
        ...token,
        value: formatBNToString(coin.value, token.decimals, formattedDecimals),
      }
    }),
    poolType,
  }
  // const hasShare = !!userShareData?.usdBalance.gt("0")
  const isMetapool = isMetaPool(formattedData.name)
  const navigate = useNavigate()
  const goToCard = useCallback(() => {
    navigate(route)
  }, [navigate, route])

  return (
    <CustomCard
      withGradient
      variant="darkest-gray"
      p={0}
      className="pool-overview-container"
      onClick={goToCard}
    >
      <Box
        // className={classNames("poolOverview", {
        //   outdated: isOutdated || shouldMigrate,
        // })}
        className="poolOverview"
      >
        <img src={isMetapool ? metaPoolHeader : basePoolHeader} width="100%" />
        {boostData?.isBoosted && (
          <BoostedTag position="absolute" top="12px" right="12px" />
        )}
        <Box textAlign="center" mt={2} mb={3}>
          <img src={lpToken.icon} height={24} />
          {isMetapool ? (
            <Tooltip title={<React.Fragment>{t("metapool")}</React.Fragment>}>
              <Box height="fit-content">
                <Text variant="h4" className="underline" textAlign="center">
                  {formattedData.name}
                </Text>
              </Box>
            </Tooltip>
          ) : (
            <Text variant="h4" textAlign="center">
              {formattedData.name}
            </Text>
          )}
        </Box>
        <Box
          className="gradient-seperator"
          height="1px"
          width="87%"
          mx="auto"
        />
        <Box p={3}>
          <Grid container>
            <Grid item xs={8}>
              <Text color="dim" size="micro">
                {t("Assets")}
              </Text>
              <Box mt={0.5}>
                {pool.poolTokens.map((token) => token.symbol).join(", ")}
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Text color="dim" size="micro">
                {t("Type")}
              </Text>
              <Text
                gradient="gradient-blue"
                mt={0.5}
                p="4px"
                borderRadius="10px"
                display="inline-block"
              >
                {isMetapool ? t("Meta") : t("Base")}
              </Text>
            </Grid>
          </Grid>
          <Grid mt={2} container rowSpacing={3}>
            <Grid item xs={6}>
              <Text color="dim" size="micro">
                {t("TVL")}
              </Text>
              <Box mt={0.5}>{`$${formattedData.reserve}`}</Box>
            </Grid>
            <Grid item xs={6}>
              <Box display="inline-block">
                <Text color="dim" size="micro">
                  {t("24h Volume")}
                </Text>
                <Box mt={0.5}>{formattedData.volume}</Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box display="inline-block">
                <Text color="dim" size="micro">
                  {t("Pool Rewards /Day")}
                </Text>
                {formattedData.rewardsPerDay.map((rewardPerDay) => {
                  return (
                    <Box mt={0.5} key={rewardPerDay.symbol}>
                      {`${rewardPerDay.symbol} ${rewardPerDay.value}`}
                    </Box>
                  )
                })}
              </Box>
            </Grid>
            <Grid item xs={6}></Grid>
            <Grid item xs={6}>
              <Text color="dim" size="micro">
                {t("Current APR")}
              </Text>
              <Box mt={0.5}>{formattedData.boostedApr}</Box>
            </Grid>
            <Grid item xs={6}>
              <Text color="dim" size="micro">
                {t("Potential Max APR")}
              </Text>
              <Box mt={0.5} display="flex" gap={0.5} alignItems="center">
                <img src={RocketIcon} height={12} />
                <Text color="#00A68C">{formattedData.maxApr}</Text>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </CustomCard>
  )
}
